import type { CSSProperties, ReactNode } from 'react'

/**
 * State, never action. A chip says what something *is*; a `Button` or an
 * `Action` says what you can do to it — so a chip never takes the ember
 * treatment, however important the state it is reporting.
 *
 * Every tone is translucent, deliberately: the card's own gradient reads
 * through the chip, and nothing in the product ends up a flat block of colour.
 * Flat fill is reserved for marks — the 6px status dots, toggle knobs, step
 * dots, the notification badge.
 *
 * Never label a wash with --color-accent. The accent tokens don't change
 * between themes, so the label would sit at the same hue as its own fill
 * (~3.4:1 dark, ~2.1:1 light). --w-pill-ink exists for exactly this.
 */

export type ChipTone =
  /** Happening now — in rehearsal, tech week, overdue. The one filled tone. */
  | 'live'
  /** Coming, and worth noticing — confirmed, sent. */
  | 'amber'
  /** Provisional — pencilled, draft. Dashed, because it isn't settled. */
  | 'quiet'
  /** Over and dealt with — wrapped, paid. */
  | 'done'
  /** Declined, failed. The one warm-failure use outside a confirmation. */
  | 'fail'

const TONES: Record<ChipTone, CSSProperties> = {
  live: {
    background: 'var(--w-pill-fill)',
    border: '1px solid var(--w-pill-line)',
    color: 'var(--w-pill-ink)',
  },
  amber: {
    background: 'transparent',
    border: '1px solid var(--w-amber-line)',
    color: 'var(--color-amber-ink)',
  },
  quiet: {
    background: 'transparent',
    border: '1px dashed var(--w-quiet)',
    color: 'var(--color-muted)',
  },
  done: {
    background: 'transparent',
    border: '1px solid var(--w-line-strong)',
    color: 'var(--color-muted)',
  },
  fail: {
    background: 'var(--w-danger-fill)',
    border: '1px solid var(--w-danger-line)',
    color: 'var(--w-danger-ink)',
  },
}

interface StatusChipProps {
  tone: ChipTone
  children: ReactNode
  /** The dense variant used inside table rows: 10px at .07em rather than 11/.09. */
  dense?: boolean
  style?: CSSProperties
  className?: string
}

export function StatusChip({ tone, children, dense = false, style, className = '' }: StatusChipProps) {
  return (
    <span
      className={className}
      style={{
        padding: dense ? '3px 9px' : '4px 9px',
        borderRadius: 99,
        font: dense ? '600 10px Manrope,sans-serif' : '600 11px Manrope,sans-serif',
        letterSpacing: dense ? '.07em' : '.09em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
        flex: 'none',
        ...TONES[tone],
        ...style,
      }}
    >
      {children}
    </span>
  )
}
